import React, { useEffect, useState, useRef } from 'react';
import { organizationService } from '../../services/organizationService';
import { useUser } from '../../context/UserContext';
import ConfirmationModal from '../ConfirmationModal';

interface XeroAuthorization {
  is_authorized: boolean;
  authorization_status: string | null;
  authorized_at: string | null;
  integration_data: any;
  integration_id: string | null;
}

interface CustomerAnalyses {
  total_customer_analyses: number;
  last_analyzed_at: string | null;
}

interface OnboardingStatus {
  xero_authorization: XeroAuthorization | null;
  customer_analyses: CustomerAnalyses;
}

interface OnboardingCardProps {
  onboardingStatus: OnboardingStatus | null;
  loading: boolean;
  error: string | null;
  onAuthorizeXero: () => void;
  onViewDetails: () => void;
  organizationId?: string;
}

const OnboardingCard: React.FC<OnboardingCardProps> = ({
  onboardingStatus,
  loading,
  error,
  onAuthorizeXero,
  onViewDetails,
  organizationId
}) => {
  const { accessToken, setUserAndToken } = useUser();
  const [publicShare, setPublicShare] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const [gmailStatus, setGmailStatus] = useState<{
    is_authorized: boolean;
    gmail_contact_email: string | null;
    gmail_contact_name: string | null;
    authorized_at: string | null;
    token_expires_at: string | null;
  } | null>(null);
  const [gmailBusy, setGmailBusy] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [loadingGmail, setLoadingGmail] = useState(true);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const focusHandlerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!organizationId) return;
      try {
        const share = await organizationService.getActiveOnboardingPublicShare(organizationId, accessToken, setUserAndToken);
        setPublicShare(share);
      } catch (e) {
        // ignore
      }
    };
    load();
  }, [organizationId, accessToken, setUserAndToken]);

  useEffect(() => {
    const loadGmailStatus = async () => {
      if (!organizationId) {
        setLoadingGmail(false);
        return;
      }
      try {
        setLoadingGmail(true);
        const status = await organizationService.getGmailAuthorizationStatus(organizationId, accessToken, setUserAndToken);
        setGmailStatus(status);
      } catch (e) {
        // If error, assume not authorized
        setGmailStatus({ is_authorized: false, gmail_contact_email: null, gmail_contact_name: null, authorized_at: null, token_expires_at: null });
      } finally {
        setLoadingGmail(false);
      }
    };
    loadGmailStatus();

    // Cleanup polling on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (focusHandlerRef.current) {
        window.removeEventListener('focus', focusHandlerRef.current);
        focusHandlerRef.current = null;
      }
    };
  }, [organizationId, accessToken, setUserAndToken]);

  const createShare = async () => {
    if (!organizationId) return;
    try {
      setBusy(true);
      const created = await organizationService.createOnboardingPublicShare(organizationId, accessToken, setUserAndToken);
      setPublicShare(created);
    } catch (e) {
      // TODO: surface toast in parent
    } finally {
      setBusy(false);
    }
  };

  const copyShare = async () => {
    if (!publicShare?.id && !publicShare?.url) return;
    const origin = window.location.origin || 'https://dashboard.meet-aria.com';
    
    // Prefer organization-based URL if available, otherwise use token-based URL
    const url = publicShare.org_url || publicShare.url || `${origin}/public/${publicShare.id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {}
  };

  const revokeShare = async () => {
    if (!organizationId) return;
    try {
      setBusy(true);
      await organizationService.revokeOnboardingPublicShare(organizationId, accessToken, setUserAndToken);
      setPublicShare(null);
    } catch (e) {
      // TODO: toast
    } finally {
      setBusy(false);
    }
  };

  const setupGmail = async () => {
    if (!organizationId) return;
    try {
      setGmailBusy(true);
      const { authorization_url } = await organizationService.getGmailAuthorizationUrl(organizationId, accessToken, setUserAndToken);
      // Open OAuth URL in new tab
      window.open(authorization_url, '_blank');
      
      // Poll for status update - check every 2 seconds for up to 2 minutes
      let pollCount = 0;
      const maxPolls = 60; // 2 minutes (60 * 2 seconds)
      
      const pollInterval = setInterval(async () => {
        pollCount++;
        try {
          const status = await organizationService.getGmailAuthorizationStatus(organizationId, accessToken, setUserAndToken);
          setGmailStatus(status);
          
          // Stop polling if authorized
          if (status.is_authorized) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setGmailBusy(false);
            if (focusHandlerRef.current) {
              window.removeEventListener('focus', focusHandlerRef.current);
              focusHandlerRef.current = null;
            }
          }
        } catch (e) {
          // ignore
        }
        
        // Stop polling after max attempts
        if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
          pollIntervalRef.current = null;
          setGmailBusy(false);
          if (focusHandlerRef.current) {
            window.removeEventListener('focus', focusHandlerRef.current);
            focusHandlerRef.current = null;
          }
        }
      }, 2000);
      
      pollIntervalRef.current = pollInterval;
      
      // Also check when window regains focus (user might have completed auth and come back)
      const handleFocus = async () => {
        try {
          const status = await organizationService.getGmailAuthorizationStatus(organizationId, accessToken, setUserAndToken);
          setGmailStatus(status);
          if (status.is_authorized) {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            setGmailBusy(false);
            window.removeEventListener('focus', handleFocus);
            focusHandlerRef.current = null;
          }
        } catch (e) {
          // ignore
        }
      };
      
      window.addEventListener('focus', handleFocus);
      focusHandlerRef.current = handleFocus;
      
      // Cleanup after max time
      setTimeout(() => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        if (focusHandlerRef.current) {
          window.removeEventListener('focus', focusHandlerRef.current);
          focusHandlerRef.current = null;
        }
        setGmailBusy(false);
      }, 120000); // 2 minutes max
    } catch (e) {
      // TODO: toast
      setGmailBusy(false);
    }
  };

  const handleRevokeGmail = async () => {
    if (!organizationId) return;
    try {
      setGmailBusy(true);
      await organizationService.revokeGmailAuthorization(organizationId, accessToken, setUserAndToken);
      setGmailStatus({ is_authorized: false, gmail_contact_email: null, gmail_contact_name: null, authorized_at: null, token_expires_at: null });
      setShowRevokeModal(false);
    } catch (e) {
      // TODO: toast
    } finally {
      setGmailBusy(false);
    }
  };
  const getOnboardingStatus = () => {
    if (!onboardingStatus) return { status: 'Not Started', showButton: true, buttonText: 'Authorize Xero' };
    
    const { xero_authorization, customer_analyses } = onboardingStatus;
    
    // If xero_authorization is null or empty
    if (!xero_authorization) {
      return { status: 'Not Started', showButton: true, buttonText: 'Authorize Xero' };
    }
    
    // Check if authorization was revoked
    if (!xero_authorization.is_authorized && xero_authorization.authorization_status === 'revoked') {
      return { status: 'Authorization Revoked', showButton: true, buttonText: 'Re-authorize Xero' };
    }
    
    // If is_authorized is false (but not revoked)
    if (!xero_authorization.is_authorized) {
      return { status: 'Authorizing Xero', showButton: false, buttonText: '' };
    }
    
    // If is_authorized is true but last_analyzed_at is null
    if (xero_authorization.is_authorized && !customer_analyses.last_analyzed_at) {
      return { status: 'Analyzing Customers', showButton: false, buttonText: '' };
    }
    
    // If last_analyzed_at is not null
    if (customer_analyses.last_analyzed_at) {
      return { status: 'Confirming Messages (SOA, Overdue)', showButton: false, buttonText: '' };
    }
    
    return { status: 'Not Started', showButton: true, buttonText: 'Authorize Xero' };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Not Started':
        return 'text-red-600 bg-red-100';
      case 'Authorization Revoked':
        return 'text-red-600 bg-red-100';
      case 'Authorizing Xero':
        return 'text-yellow-600 bg-yellow-100';
      case 'Analyzing Customers':
        return 'text-blue-600 bg-blue-100';
      case 'Confirming Messages (SOA, Overdue)':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const { status, showButton, buttonText } = getOnboardingStatus();

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Onboarding</h3>
        <div className="flex items-center justify-center h-20">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading onboarding status...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Onboarding</h3>
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
          Error loading onboarding status: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Onboarding</h3>
        <button
          onClick={onViewDetails}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
        >
          View Details →
        </button>
      </div>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-500">Current Status:</span>
          <div className="flex items-center space-x-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
              {status}
            </span>
            {showButton && (
              <button
                onClick={onAuthorizeXero}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                {buttonText}
              </button>
            )}
          </div>
        </div>

        {/* Public Upcoming Dunning Page Section */}
        {organizationId && (
          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <span className="text-sm font-medium text-gray-500">Public Upcoming Dunning Page:</span>
            <div className="flex items-center space-x-2">
              {publicShare ? (
                <>
                  <button
                    onClick={copyShare}
                    disabled={busy}
                    className="px-3 py-1.5 bg-gray-100 text-gray-800 text-xs font-medium rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Copy Public Link
                  </button>
                  <button
                    onClick={revokeShare}
                    disabled={busy}
                    className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Revoke
                  </button>
                </>
              ) : (
                <button
                  onClick={createShare}
                  disabled={busy}
                  className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Public Link
                </button>
              )}
            </div>
          </div>
        )}

        {/* Customer Contact Email Section */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <span className="text-sm font-medium text-gray-500">Email ARiA uses to contact the customers:</span>
          <div className="flex items-center space-x-3">
            {loadingGmail ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                <span className="text-xs text-gray-500">Loading...</span>
              </div>
            ) : gmailStatus?.is_authorized && gmailStatus.gmail_contact_email ? (
              <>
                <div className="flex flex-col items-end">
                  {gmailStatus.gmail_contact_name && (
                    <span className="text-sm font-medium text-gray-900">{gmailStatus.gmail_contact_name}</span>
                  )}
                  <span className="text-sm text-gray-700">{gmailStatus.gmail_contact_email}</span>
                </div>
                <button
                  onClick={() => setShowRevokeModal(true)}
                  disabled={gmailBusy}
                  className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Revoke
                </button>
              </>
            ) : (
              <button
                onClick={setupGmail}
                disabled={gmailBusy || !organizationId}
                className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {gmailBusy ? 'Opening...' : 'Setup'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Revoke Gmail Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRevokeModal}
        onClose={() => setShowRevokeModal(false)}
        title="Revoke Gmail Authorization"
        message={`Are you sure you want to revoke Gmail authorization for ${gmailStatus?.gmail_contact_name ? `${gmailStatus.gmail_contact_name} (${gmailStatus.gmail_contact_email})` : gmailStatus?.gmail_contact_email}? This will prevent the system from sending emails on behalf of this organization.`}
        showWarningIcon={true}
        actions={[
          {
            label: 'Cancel',
            onClick: () => setShowRevokeModal(false),
            variant: 'secondary'
          },
          {
            label: 'Revoke',
            onClick: handleRevokeGmail,
            variant: 'danger',
            disabled: gmailBusy
          }
        ]}
      />
    </div>
  );
};

export default OnboardingCard;
